import {
  INoteCellLinkOffsets,
  NoteCellLinkConfidenceBasis,
  NoteCellLinkSource,
  NoteCellLinkStatus,
} from '@common/interfaces/noteCellLink'
import {
  ICellNoteIndicator,
  ICellNoteIndicatorsResponse,
  INote,
  getCellKey,
} from '@common/interfaces/notes'
import { Action } from 'redux'
import * as notesActions from '../notes/actions'
import { ILoadingData } from '../types'
import * as actions from './actions'

/** A note→cell link row as returned by ListLinksForCell (note-sourced or established fact). */
export interface ICellNoteLinkRow {
  id: string
  noteId: string | null
  noteText: string | null
  label: string | null
  displayText: string | null
  rowId: string
  columnId: string
  /** The column the source note was authored on; null for a link with no note behind it. */
  sourceColumnId: string | null
  recordDate: string | null
  source: NoteCellLinkSource
  confidenceBasis: NoteCellLinkConfidenceBasis | null
  matchedPhrase: string | null
  matchOffsets: INoteCellLinkOffsets[] | null
  status: NoteCellLinkStatus
  /** The slice of the note this suggestion stands for; null until segmentation has run. */
  segmentText: string | null
  segmentStart: number | null
  segmentEnd: number | null
}

/** A note authored directly on the cell, distinct from a link attributed to it. */
export interface ICellAuthoredNote {
  id: string
  noteText: string | null
  createdAt: string
}

export interface ICellNoteLinksResponse {
  links: ICellNoteLinkRow[]
  notes: ICellAuthoredNote[]
  count: number
}

/** Key for the per-cell links cache. Mirrors getCellKey but namespaced by table type. */
export const getCellLinksKey = (
  tableType: string,
  rowId: string,
  columnId: string,
  recordDate?: string | null,
): string => `${tableType}|${getCellKey(rowId, columnId, recordDate)}`

export interface ICellNotesState {
  // Only store lightweight indicators by table
  indicatorsByTable: Record<string, ILoadingData<ICellNoteIndicatorsResponse>>
  // Full links (suggested + confirmed + established facts) per focused cell
  linksByCell: Record<string, ILoadingData<ICellNoteLinksResponse>>
  /**
   * When each table last had a note saved with suggestions still awaiting segmentation. Those
   * are withheld by the server until they have a slice, so nothing points at them yet; the
   * table watches its own entry to come back for the markers once the job has landed. Keyed
   * like `indicatorsByTable`, so one table's pending work never makes another one poll.
   */
  segmentationPendingByTable: Record<string, number>
}

const initialLoadingData: ILoadingData<ICellNoteIndicatorsResponse> = {
  isLoading: false,
  isSaving: false,
  isSaved: false,
  data: null,
}

type CellNotesAction = Action & {
  data: INote &
    ICellNoteIndicatorsResponse &
    Partial<ICellNoteLinksResponse> & {
      tableType?: string
      clientId?: string
      cellNoteId?: string
      id?: string
      status?: string
      // Cell coordinates carried by a confirm/dismiss response, which returns the link row.
      rowId?: string
      columnId?: string
      recordDate?: string | null
      // Links generated server-side during a note create, on cells other than the note's own.
      createdCellLinks?: {
        rowId: string
        columnId: string
        recordDate: string | null
        status: NoteCellLinkStatus
      }[]
      /** Some created suggestions are still waiting on the segmentation job. */
      pendingCellLinkSegmentation?: boolean
      /** What approving did to the note the words came from. */
      sourceNote?: {
        id: string
        deleted: boolean
        rowId: string | null
        columnId: string | null
        recordDate: string | null
      }
    }
  params: Record<string, unknown>
}

const initialState: ICellNotesState = {
  indicatorsByTable: {},
  linksByCell: {},
  segmentationPendingByTable: {},
}

const initialLinksLoadingData: ILoadingData<ICellNoteLinksResponse> = {
  isLoading: false,
  isSaving: false,
  isSaved: false,
  data: null,
}

/** The links cache key for a list action, derived from the params the action carried. */
const getLinksCellKeyFromParams = (params: Record<string, unknown> | undefined): string => {
  const { tableType, rowId, columnId, recordDate } = (params || {}) as {
    tableType?: string
    rowId?: string
    columnId?: string
    recordDate?: string
  }
  return getCellLinksKey(tableType || '', rowId || '', columnId || '', recordDate)
}

// Replaces a link (by id) across every cached cell, or removes it when `remove` is set.
const updateLinkAcrossCells = (
  linksByCell: ICellNotesState['linksByCell'],
  linkId: string,
  updater: (link: ICellNoteLinkRow) => ICellNoteLinkRow | null,
): ICellNotesState['linksByCell'] => {
  const next: ICellNotesState['linksByCell'] = {}
  for (const [key, entry] of Object.entries(linksByCell)) {
    if (!entry.data) {
      next[key] = entry
      continue
    }
    const links = entry.data.links
      .map((link) => (link.id === linkId ? updater(link) : link))
      .filter((link): link is ICellNoteLinkRow => link !== null)
    const notes = entry.data.notes
    next[key] = { ...entry, data: { links, notes, count: links.length + notes.length } }
  }
  return next
}

/**
 * Applies a review decision to one table's indicator cache.
 *
 * Scoped to a single `${tableType}|${clientId}` entry on purpose. Row and column ids come
 * from the shared canonical vocabulary, so a key like "Gross Margin|Mar 2025" exists for
 * every client that has that statement — matching on coordinates alone would move another
 * loaded client's marker for a decision made on this one. The decision therefore carries the
 * table it was made in; without it we leave the cache alone and let the next load correct it,
 * rather than guessing.
 */
const applyDecisionToIndicators = (
  indicatorsByTable: ICellNotesState['indicatorsByTable'],
  tableKey: string | null,
  link: { rowId?: string; columnId?: string; recordDate?: string | null },
  isConfirmed: boolean,
): ICellNotesState['indicatorsByTable'] => {
  if (!tableKey) return indicatorsByTable

  const entry = indicatorsByTable[tableKey]
  if (!entry?.data) return indicatorsByTable

  const cellKey = getCellKey(link.rowId || '', link.columnId || '', link.recordDate ?? null)
  const current = entry.data.indicatorsByCell?.[cellKey]
  if (!current) return indicatorsByTable

  const updated: ICellNoteIndicator = {
    ...current,
    suggestedCount: Math.max(0, (current.suggestedCount ?? 0) - 1),
    confirmedLinkCount: isConfirmed
      ? (current.confirmedLinkCount ?? 0) + 1
      : (current.confirmedLinkCount ?? 0),
  }

  return {
    ...indicatorsByTable,
    [tableKey]: {
      ...entry,
      data: {
        ...entry.data,
        indicatorsByCell: { ...entry.data.indicatorsByCell, [cellKey]: updated },
        indicators: entry.data.indicators.map((indicator) =>
          getCellKey(indicator.rowId, indicator.columnId, indicator.recordDate ?? null) === cellKey
            ? updated
            : indicator,
        ),
      },
    },
  }
}

/**
 * Drops the header note from its cell's marker once approving has moved the last of its words.
 *
 * The decision handler above only touches the cell the words went TO. Nothing else tells the
 * grid that the note they came FROM is gone, so its marker sat there until a reload.
 */
const dropDeletedSourceNote = (
  indicatorsByTable: ICellNotesState['indicatorsByTable'],
  tableKey: string | null,
  source: { rowId: string | null; columnId: string | null; recordDate: string | null },
): ICellNotesState['indicatorsByTable'] => {
  if (!tableKey) return indicatorsByTable

  const entry = indicatorsByTable[tableKey]
  if (!entry?.data) return indicatorsByTable

  const cellKey = getCellKey(source.rowId || '', source.columnId || '', source.recordDate ?? null)
  const current = entry.data.indicatorsByCell?.[cellKey]
  if (!current) return indicatorsByTable

  const noteCount = Math.max(0, (current.noteCount ?? 0) - 1)
  const updated: ICellNoteIndicator = { ...current, noteCount }
  // A cell with no note and nothing attributed has no marker left to draw, so the entry goes
  // rather than lingering as a zero the renderer still has to reason about.
  const isEmpty =
    noteCount === 0 &&
    !updated.suggestedCount &&
    !updated.establishedCount &&
    !updated.confirmedLinkCount

  const indicatorsByCell = { ...entry.data.indicatorsByCell }
  if (isEmpty) delete indicatorsByCell[cellKey]
  else indicatorsByCell[cellKey] = updated

  return {
    ...indicatorsByTable,
    [tableKey]: {
      ...entry,
      data: {
        ...entry.data,
        indicatorsByCell,
        indicators: Object.values(indicatorsByCell),
      },
    },
  }
}

/** The indicator cache a decision belongs to, from the params the action carried. */
const decisionTableKey = (params: Record<string, unknown> | undefined): string | null => {
  const tableType = params?.tableType
  const clientId = params?.clientId
  if (typeof tableType !== 'string' || typeof clientId !== 'string') return null
  return `${tableType}|${clientId}`
}

/**
 * Folds the links a note-create just produced into the table's indicator cache.
 *
 * The links land on OTHER cells than the one the note was written on — a note on a column
 * header attributes to the line rows its text names — so nothing else in the client learns
 * about them, and the new markers only appeared after a reload. The server returns them on
 * the create response precisely so this can happen without another request.
 */
const addCreatedLinksToIndicators = (
  data: ICellNoteIndicatorsResponse,
  links: {
    rowId: string
    columnId: string
    recordDate: string | null
    status: NoteCellLinkStatus
  }[],
): ICellNoteIndicatorsResponse => {
  if (links.length === 0) return data

  const byCell = { ...data.indicatorsByCell }

  for (const link of links) {
    const cellKey = getCellKey(link.rowId, link.columnId, link.recordDate)
    const current = byCell[cellKey]
    // Auto-confirm can create an exact match already confirmed, which belongs on the solid
    // marker rather than in the review queue.
    const isConfirmed = link.status === NoteCellLinkStatus.Confirmed

    byCell[cellKey] = current
      ? {
          ...current,
          suggestedCount: (current.suggestedCount ?? 0) + (isConfirmed ? 0 : 1),
          confirmedLinkCount: (current.confirmedLinkCount ?? 0) + (isConfirmed ? 1 : 0),
        }
      : {
          cellNoteId: '',
          rowId: link.rowId,
          columnId: link.columnId,
          recordDate: link.recordDate,
          noteCount: 0,
          lastUpdated: new Date(),
          hasUnresolved: false,
          suggestedCount: isConfirmed ? 0 : 1,
          establishedCount: 0,
          establishedNoteCount: 0,
          confirmedLinkCount: isConfirmed ? 1 : 0,
        }
  }

  return { ...data, indicatorsByCell: byCell, indicators: Object.values(byCell) }
}

const cellNotesReducer = (
  state: ICellNotesState = initialState,
  action: CellNotesAction,
): ICellNotesState => {
  const { type } = action

  // Handle success actions for indicators
  if (type === actions.LIST_CELL_NOTE_INDICATORS_SUCCESS) {
    const tableKey = `${action.data.tableType || ''}|${action.data.clientId || ''}`
    return {
      ...state,
      indicatorsByTable: {
        ...state.indicatorsByTable,
        [tableKey]: {
          ...(state.indicatorsByTable[tableKey] || initialLoadingData),
          data: action.data,
        },
      },
    }
  }

  // Full links for a focused cell. REQUEST/FAILURE are handled alongside SUCCESS so the
  // panel can show a real loading state and an error instead of an empty list: without them
  // isLoading was only ever written as false, so it never became true.
  if (
    type === actions.LIST_CELL_NOTE_LINKS_REQUEST ||
    type === actions.LIST_CELL_NOTE_LINKS_FAILURE
  ) {
    const cellKey = getLinksCellKeyFromParams(action.params)
    const existing = state.linksByCell[cellKey]

    return {
      ...state,
      linksByCell: {
        ...state.linksByCell,
        [cellKey]: {
          ...(existing || initialLinksLoadingData),
          // Keep whatever was already loaded so a refresh doesn't blank the open panel.
          data: existing?.data ?? null,
          isLoading: type === actions.LIST_CELL_NOTE_LINKS_REQUEST,
        },
      },
    }
  }

  if (type === actions.LIST_CELL_NOTE_LINKS_SUCCESS) {
    const cellKey = getLinksCellKeyFromParams(action.params)
    const linksData: ICellNoteLinksResponse = {
      links: action.data.links ?? [],
      notes: action.data.notes ?? [],
      count: action.data.count ?? 0,
    }
    return {
      ...state,
      linksByCell: {
        ...state.linksByCell,
        [cellKey]: { isLoading: false, isSaving: false, isSaved: false, data: linksData },
      },
    }
  }

  // Confirm: flip the link's status wherever it is cached, and move the cell's marker with
  // it so the dashed ring gives way to the solid one immediately.
  if (type === actions.CONFIRM_CELL_NOTE_LINK_SUCCESS && action.data?.id) {
    const tableKey = decisionTableKey(action.params)
    const source = action.data.sourceNote
    const afterDecision = applyDecisionToIndicators(
      state.indicatorsByTable,
      tableKey,
      action.data,
      true,
    )

    return {
      ...state,
      linksByCell: updateLinkAcrossCells(state.linksByCell, action.data.id, (link) => ({
        ...link,
        status: NoteCellLinkStatus.Confirmed,
      })),
      indicatorsByTable: source?.deleted
        ? dropDeletedSourceNote(afterDecision, tableKey, source)
        : afterDecision,
    }
  }

  // Dismiss: drop the link (dismissals are global and never re-shown) and take its marker
  // with it, rather than leaving a review signal for something already decided.
  if (type === actions.DISMISS_CELL_NOTE_LINK_SUCCESS && action.data?.id) {
    return {
      ...state,
      linksByCell: updateLinkAcrossCells(state.linksByCell, action.data.id, () => null),
      indicatorsByTable: applyDecisionToIndicators(
        state.indicatorsByTable,
        decisionTableKey(action.params),
        action.data,
        false,
      ),
    }
  }

  if (type === notesActions.ADD_NOTE_SUCCESS) {
    const note: INote = action.data
    // Replies carry their parent's `cellNoteId`, so they arrive here looking
    // like cell notes — but the indicator counts thread ROOTS only (see
    // `note.parent_id IS NULL` in services/cellNotes/ListIndicators.ts). Without
    // this guard every reply inflates the badge until the next refetch corrects
    // it, and nested threads make that drift much larger.
    if (!note.cellNote || note.parentId) {
      return state
    }

    const tableKey = `${note.cellNote?.tableType || ''}|${note.clientInfo?.id || ''}`
    const currentTableData = state.indicatorsByTable[tableKey]

    // Suggestions still being segmented are withheld by the server, so none of them are in
    // `createdCellLinks` and nothing below will draw them. Stamping the save is what lets this
    // table ask again once the job has landed, instead of waiting for a reload.
    const segmentationPendingByTable = action.data.pendingCellLinkSegmentation
      ? { ...state.segmentationPendingByTable, [tableKey]: Date.now() }
      : state.segmentationPendingByTable

    if (currentTableData && currentTableData.data) {
      const cellKey = getCellKey(
        note.cellNote?.rowId,
        note.cellNote?.columnId,
        note.cellNote?.recordDate,
      )
      const currentIndicator = currentTableData.data.indicatorsByCell[cellKey]

      // Update or create indicator for this cell
      const updatedIndicator: ICellNoteIndicator = {
        cellNoteId: action.data.cellNoteId,
        rowId: note.cellNote?.rowId,
        columnId: note.cellNote?.columnId,
        recordDate: note.cellNote?.recordDate,
        noteCount: currentIndicator ? currentIndicator.noteCount + 1 : 1,
        lastUpdated: new Date(),
        hasUnresolved: true, // New notes are typically unresolved
        // Attribution is derived server-side; carry whatever the last load knew rather than
        // guessing, so writing a note never clears a pending review signal on the cell.
        suggestedCount: currentIndicator?.suggestedCount ?? 0,
        establishedCount: currentIndicator?.establishedCount ?? 0,
        establishedNoteCount: currentIndicator?.establishedNoteCount ?? 0,
        confirmedLinkCount: currentIndicator?.confirmedLinkCount ?? 0,
      }

      const withNote: ICellNoteIndicatorsResponse = {
        ...currentTableData.data,
        indicatorsByCell: {
          ...currentTableData.data.indicatorsByCell,
          [cellKey]: updatedIndicator,
        },
        indicators: [
          ...currentTableData.data.indicators.filter(
            (ind) => getCellKey(ind.rowId, ind.columnId, ind.recordDate) !== cellKey,
          ),
          updatedIndicator,
        ],
        count:
          currentTableData.data.indicators.filter(
            (ind) => getCellKey(ind.rowId, ind.columnId, ind.recordDate) !== cellKey,
          ).length + 1,
      }

      return {
        ...state,
        segmentationPendingByTable,
        indicatorsByTable: {
          ...state.indicatorsByTable,
          [tableKey]: {
            ...currentTableData,
            data: addCreatedLinksToIndicators(withNote, action.data.createdCellLinks ?? []),
          },
        },
      }
    }

    // No indicator cache to fold the note into, but the pending stamp still has to land: the
    // table may load between the save and the job finishing.
    return { ...state, segmentationPendingByTable }
  }

  if (type === notesActions.DELETE_NOTE_SUCCESS) {
    const note: INote = action.data
    // Mirror of the ADD guard: deleting a reply leaves the root count untouched.
    if (!note.cellNote || note.parentId) {
      return state
    }

    const tableKey = `${note.cellNote?.tableType || ''}|${note.clientInfo?.id || ''}`
    const currentTableData = state.indicatorsByTable[tableKey]

    if (currentTableData && currentTableData.data) {
      const cellKey = getCellKey(
        note.cellNote?.rowId,
        note.cellNote?.columnId,
        note.cellNote?.recordDate,
      )
      const currentIndicator = currentTableData.data.indicatorsByCell[cellKey]

      if (currentIndicator) {
        const newNoteCount = currentIndicator.noteCount - 1

        if (newNoteCount <= 0) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [cellKey]: _removed, ...remainingIndicatorsByCell } =
            currentTableData.data.indicatorsByCell

          return {
            ...state,
            indicatorsByTable: {
              ...state.indicatorsByTable,
              [tableKey]: {
                ...currentTableData,
                data: {
                  ...currentTableData.data,
                  indicatorsByCell: remainingIndicatorsByCell,
                  indicators: currentTableData.data.indicators.filter(
                    (ind) => getCellKey(ind.rowId, ind.columnId, ind.recordDate) !== cellKey,
                  ),
                  count: currentTableData.data.count - 1,
                },
              },
            },
          }
        } else {
          const updatedIndicator: ICellNoteIndicator = {
            ...currentIndicator,
            noteCount: newNoteCount,
            lastUpdated: new Date(),
          }

          return {
            ...state,
            indicatorsByTable: {
              ...state.indicatorsByTable,
              [tableKey]: {
                ...currentTableData,
                data: {
                  ...currentTableData.data,
                  indicatorsByCell: {
                    ...currentTableData.data.indicatorsByCell,
                    [cellKey]: updatedIndicator,
                  },
                  indicators: currentTableData.data.indicators.map((ind) =>
                    getCellKey(ind.rowId, ind.columnId, ind.recordDate) === cellKey
                      ? updatedIndicator
                      : ind,
                  ),
                },
              },
            },
          }
        }
      }
    }
  }

  return state
}

export default cellNotesReducer
